import { ENGINE_MMI_2026, ENGINE_MMI_2026_CORRECTED } from '../../engine.js';
import { ipmt, pmt } from '../../excel.js';
/** Dans l'ordre des colonnes du classeur : H, I, K, L, N, O. */
export const FR_REGIMES = [
    'micro-foncier',
    'foncier-reel',
    'micro-bic',
    'lmnp-reel',
    'sci-ir',
    'sci-is',
];
/** P3 : LMNP longue durée — abattement de 50 %, plafond de 77 700 € de loyers. */
export const MICRO_BIC_LONG_TERM = { allowance: 0.5, ceiling: 77_700 };
/** P3 : meublé de tourisme non classé (LF 2025) — abattement de 30 %, plafond de 15 000 €. */
export const MICRO_BIC_TOURISM_UNCLASSIFIED = { allowance: 0.3, ceiling: 15_000 };
/** Plafond de recettes du micro-foncier (revenus fonciers bruts annuels). */
const MICRO_FONCIER_CEILING = 15_000;
/** P2 : taux réduit de l'IS (15 %) jusqu'à 42 500 € de bénéfice, taux normal ensuite. */
const REDUCED_CORPORATE_TAX_RATE = 0.15;
const REDUCED_CORPORATE_TAX_CEILING = 42_500;
/**
 * Valeurs du classeur. `socialContributionRate` reste à 17,2 % (résident) : c'est le
 * mode `resident-fr` que les valeurs d'origine servent à valider. Pour le foyer, le
 * taux passe à 7,5 % — voir `socialContributionRateFor`.
 */
export const FISCALITE_AVANCEE_DEFAULTS = {
    purchasePrice: 90_000,
    notaryFees: 9_000,
    works: 0,
    buildingDepreciationYears: 20,
    worksDepreciationYears: 10,
    annualRentRate: 0.235,
    annualPriceGrowth: 0.05,
    holdingYears: 5,
    loanAnnualRate: 0.0387,
    loanYears: 20,
    annualCondo: 500,
    annualPropertyTax: 900,
    annualPno: 300,
    accountingLmnp: 350,
    accountingSciIs: 500,
    cfeShareOfPropertyTax: 0.4,
    personalTaxRate: 0,
    socialContributionRate: 0.172,
    corporateTaxRate: 0.15,
    microFoncierAllowance: 0.3,
    microBicAllowance: 0.3,
};
/** `mmi-2026-corrected` : `corporateTaxRate` = 25 % (taux normal), et non plus un taux plat. */
export const FISCALITE_CORRECTED_DEFAULTS = {
    ...FISCALITE_AVANCEE_DEFAULTS,
    corporateTaxRate: 0.25,
    microBicAllowance: MICRO_BIC_LONG_TERM.allowance,
    microBicCeiling: MICRO_BIC_LONG_TERM.ceiling,
};
const RULES = {
    'micro-foncier': {
        worksDepreciation: 'none',
        buildingDepreciation: false,
        accounting: 'none',
        cfe: false,
        base: { kind: 'allowance', allowance: 'micro-foncier' },
        corporate: false,
        bookValueBasis: false,
    },
    'foncier-reel': {
        worksDepreciation: 'half',
        buildingDepreciation: false,
        accounting: 'none',
        cfe: false,
        base: { kind: 'result' },
        corporate: false,
        bookValueBasis: false,
    },
    'micro-bic': {
        worksDepreciation: 'none',
        buildingDepreciation: false,
        accounting: 'none',
        cfe: true,
        base: { kind: 'allowance', allowance: 'micro-bic' },
        corporate: false,
        bookValueBasis: false,
    },
    'lmnp-reel': {
        worksDepreciation: 'full',
        buildingDepreciation: true,
        accounting: 'lmnp',
        cfe: true,
        base: { kind: 'result' },
        corporate: false,
        bookValueBasis: false,
    },
    'sci-ir': {
        worksDepreciation: 'half',
        buildingDepreciation: false,
        accounting: 'none',
        cfe: false,
        base: { kind: 'result' },
        corporate: false,
        bookValueBasis: false,
    },
    'sci-is': {
        worksDepreciation: 'full',
        buildingDepreciation: true,
        accounting: 'sci-is',
        cfe: true,
        base: { kind: 'result' },
        corporate: true,
        bookValueBasis: true,
    },
};
/** P3 : contrôle des plafonds de loyers des micro-régimes. */
function isEligible(regime, i, annualRent) {
    if (regime === 'micro-foncier')
        return annualRent <= MICRO_FONCIER_CEILING;
    if (regime === 'micro-bic')
        return i.microBicCeiling === undefined || annualRent <= i.microBicCeiling;
    return true;
}
/**
 * P2 : IS à taux réduit jusqu'au seuil, puis taux normal. Nul sur un résultat négatif.
 * `reducedRateEligible` à faux : taux normal sur toute la base (capital non éligible).
 */
export function corporateTax(base, normalRate, reducedRateEligible = true) {
    if (!(base > 0))
        return 0;
    if (!reducedRateEligible)
        return base * normalRate;
    const reduced = Math.min(base, REDUCED_CORPORATE_TAX_CEILING);
    return reduced * REDUCED_CORPORATE_TAX_RATE + (base - reduced) * normalRate;
}
export function compareRegimes(input, options = {}) {
    const i = input;
    const corrected = (options.engine ?? ENGINE_MMI_2026) === ENGINE_MMI_2026_CORRECTED;
    const annualRent = (i.works + i.purchasePrice) * i.annualRentRate; // F16
    const buildingDepreciation = (i.purchasePrice + i.notaryFees) / i.buildingDepreciationYears; // F12
    const worksDepreciation = i.works / i.worksDepreciationYears; // F14
    const salePrice = i.purchasePrice * (1 + i.annualPriceGrowth) ** i.holdingYears; // F17
    const cumulatedDepreciation = buildingDepreciation * i.holdingYears; // F18
    const debt = i.purchasePrice + i.works; // F20
    const annuity = pmt(i.loanAnnualRate, i.loanYears, debt); // F23
    const firstYearInterest = ipmt(i.loanAnnualRate, 1, i.loanYears, debt, 0, 0); // F24
    const firstYearPrincipal = annuity - firstYearInterest; // F25
    const regimes = {};
    for (const regime of FR_REGIMES) {
        const base = RULES[regime];
        // F12 : le classeur ne déduit que la moitié de l'amortissement des travaux en foncier
        // réel et en SCI à l'IR ; P4 : depuis la LF 2025, le LMNP réel réintègre ses amortissements
        // dans le prix de revient à la revente, comme la SCI à l'IS.
        const rules = corrected
            ? {
                ...base,
                worksDepreciation: base.worksDepreciation === 'half' ? 'full' : base.worksDepreciation,
                bookValueBasis: base.bookValueBasis || regime === 'lmnp-reel',
            }
            : base;
        const lines = {
            rent: annualRent,
            condo: -i.annualCondo,
            propertyTax: -i.annualPropertyTax,
            pno: -i.annualPno,
            interest: firstYearInterest,
            worksDepreciation: rules.worksDepreciation === 'full'
                ? -worksDepreciation
                : rules.worksDepreciation === 'half'
                    ? -worksDepreciation / 2
                    : 0,
            buildingDepreciation: rules.buildingDepreciation ? -buildingDepreciation : 0,
            accounting: rules.accounting === 'lmnp'
                ? -i.accountingLmnp
                : rules.accounting === 'sci-is'
                    ? -i.accountingSciIs
                    : 0,
            cfe: rules.cfe ? -i.annualPropertyTax * i.cfeShareOfPropertyTax : 0,
        };
        // l. 44 : SUM(33:43), dans l'ordre des lignes
        const resultBeforeTax = lines.rent +
            lines.condo +
            lines.propertyTax +
            lines.pno +
            lines.interest +
            lines.worksDepreciation +
            lines.buildingDepreciation +
            lines.accounting +
            lines.cfe;
        const taxRate = rules.corporate
            ? i.corporateTaxRate
            : i.personalTaxRate + i.socialContributionRate; // l. 48
        const allowance = rules.base.kind === 'allowance'
            ? rules.base.allowance === 'micro-foncier'
                ? i.microFoncierAllowance
                : i.microBicAllowance
            : 0;
        const taxableBase = rules.base.kind === 'allowance' ? lines.rent * (1 - allowance) : resultBeforeTax; // l. 51
        // l. 52 : SI(-base × taux < 0 ; ... ; 0). P2 : l'IS est progressif dans mmi-2026-corrected.
        const rawTax = corrected && rules.corporate
            ? -corporateTax(taxableBase, i.corporateTaxRate)
            : -taxableBase * taxRate;
        const tax = rawTax < 0 ? rawTax : 0;
        const depreciationAddBack = -lines.buildingDepreciation - lines.worksDepreciation; // l. 55
        const cashInPocket = resultBeforeTax + tax + firstYearPrincipal + depreciationAddBack; // l. 56
        const basis = rules.bookValueBasis
            ? i.purchasePrice - cumulatedDepreciation
            : i.purchasePrice; // l. 59
        const capitalGain = { basis, salePrice, gain: salePrice - basis }; // l. 60-61
        regimes[regime] = {
            regime,
            lines,
            resultBeforeTax,
            taxRate,
            allowance,
            taxableBase,
            tax,
            capitalRepayment: firstYearPrincipal,
            depreciationAddBack,
            cashInPocket,
            eligible: !corrected || isEligible(regime, i, annualRent),
            capitalGain,
        };
    }
    return {
        annualRent,
        buildingDepreciation,
        worksDepreciation,
        salePrice,
        cumulatedDepreciation,
        annuity,
        firstYearInterest,
        firstYearPrincipal,
        regimes,
    };
}
//# sourceMappingURL=regimes.js.map